<template>
	<EventLayout :event="event" :subtitle="trans('events.organizers.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-organizer-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Modal id="create-organizer-modal">
			<template #title>{{ trans('events.organizers.create') }}</template>
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
				<SearchModel v-if="!newOrganizer.user" class="lg:col-span-2" search-type="users" label="models.user" attribute="full_name" @selected="selectUser"/>
				<LabeledObject v-else class="lg:col-span-2" full>
					<template #label>{{ trans('models.user') }}</template>
					<div class="border rounded px-3 py-2 flex items-center justify-between gap-3 w-full dark:border-gray-800">
						<span>{{ newOrganizer.user.full_name }}</span>
						<CloseSelected @click.stop="deselectUser"/>
					</div>
				</LabeledObject>
				<LabeledObject full>
					<template #label>{{ trans('account.role') }}</template>
					<input v-model="newOrganizer.role" type="text" maxlength="255">
				</LabeledObject>
				<div class="flex items-end">
					<Switch v-model="newOrganizer.administrator" label="account.administrator" full/>
				</div>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<Empty v-if="organizers.length === 0"/>
		<CardGrid v-else tight>
			<template v-for="organizer in organizers" :key="`organizer-${organizer.id}`">
				<Modal :id="`edit-organizer-${organizer.id}-modal`">
					<template #title>{{ trans('events.organizers.edit') }}</template>
					<p class="text-lg font-bold">{{ organizer.user?.full_name }}</p>
					<p class="text-gray-400">{{ organizer.user?.id_number }}</p>
					<LabeledObject>
						<template #label>{{ trans('account.role') }}</template>
						<TextInput v-model="organizer.role"/>
					</LabeledObject>
					<Switch v-model="organizer.administrator" label="account.administrator"/>
					<template #footer>
						<PrimaryButton :color="Color.Green" @click.stop="update(organizer)">
							<i class="fa-solid fa-check"/>
						</PrimaryButton>
					</template>
				</Modal>
				<Card :title="organizer.user?.full_name">
					<template v-if="organizer.administrator" #badges>
						<Badge :color="Color.TrueAArEII" active>{{ trans('account.administrator') }}</Badge>
					</template>
					<LabeledObject>
						<template #label>{{ trans('account.id_number') }}</template>
						{{ organizer.user?.id_number }}
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('account.role') }}</template>
						{{ organizer.role }}
					</LabeledObject>
					<template #footer>
						<SecondaryButton :color="Color.Blue" @click.stop="showModal(`edit-organizer-${organizer.id}-modal`)">
							<i class="fa-solid fa-edit mr-2"/>
							{{ trans('general.edit') }}
						</SecondaryButton>
						<SoftButton :color="Color.Red" @click.stop="remove(organizer.id)">
							<i class="fa-solid fa-trash mr-2"/>
							{{ trans('general.delete') }}
						</SoftButton>
					</template>
				</Card>
			</template>
		</CardGrid>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {type Event} from '@/Types/Events/Event'
import Badge from '@/Components/UI/Badges/Badge.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import {type Organizer} from '@/Types/Events/Organizer'
import TextInput from '@/Components/UI/Inputs/TextInput.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import SearchModel from '@/Components/Search/SearchModel.vue'
import CloseSelected from '@/Components/Search/CloseSelected.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	event:Event,
}>()

const newOrganizer:Ref<Organizer> = ref({
	role: '',
	administrator: false,
	event_id: props.event.id,
} as Organizer)

const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const organizers:Ref<Array<Organizer>> = ref(props.event.organizers)

function selectUser(user:User){
	newOrganizer.value.user = user
	newOrganizer.value.user_id = user.id
}

function deselectUser(){
	delete newOrganizer.value.user
	delete newOrganizer.value.user_id
}

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('events.organizers.store', event.value.id), {
			user_id: newOrganizer.value.user_id,
			role: newOrganizer.value.role,
			administrator: newOrganizer.value.administrator,
		}).then(({data}) => {
			organizers.value.unshift(data)
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function update(organizer:Organizer):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.organizers.update', {event: event.value.id, organizer: organizer.id}), organizer).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function remove(organizerId:number):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.organizers.delete', {event: event.value.id, organizer: organizerId})).then(() => {
			const index = organizers.value.findIndex((organizer) => organizer.id === organizerId)
			if(index > -1){
				organizers.value.splice(index, 1)
			}
			showSuccess(trans('general.deleted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>